import React, { useEffect, useRef, createRef, useMemo } from "react";
import styled from "styled-components";
import moment from "moment";
import { Box, Flex } from "reflexbox/styled-components";

import { useApplicationStateValue } from "../../application/Application";
import { useFavoriteStateValue } from "../../favorite/Favorite";
import PanelItem from "./PanelItem";
import TitleSeparator from "../../components/title-separator/TitleSeparator";
import { withLocalizeStrings } from "../../languages/Localize";
import { tab } from "@testing-library/user-event/dist/tab";

const StyledPanelsContainer = styled.div`
    overflow: auto;
    width: 100%;
    height: 91%;
`;

const StyledContainer = styled(Box)`
    background: ${({ theme }) => theme.colors.transparent};
    height: 50%;
    margin-top: 20%;
`;
const StyledHeaderLabel = styled(Flex)`
    color: ${({ theme }) => theme.colors.white};
    text-align: center;
    justify-content: center;
    align-items: center;
    height: 100%;
    padding: ${({ theme }) => theme.space[3]}px;
`;

const FavouritePanels = ({ strings }) => {
    const { favoritePanels, removeFromFavorites } = useFavoriteStateValue();
    const {
        appData: { forumPanels }
    } = useApplicationStateValue();
    const refs = useRef(favoritePanels.map(() => createRef()));
    const panelsContainerRef = useRef();

    const panelsAsArrayWithId = useMemo(() => {
        return favoritePanels
            .reduce((acc, el) => {
                const panel = forumPanels[el];
                acc.push({ ...panel, id: el });
                return acc;
            }, [])
            .sort((a, b) => {
                return moment(a.startTime) - moment(b.startTime);
            });
    }, [forumPanels, favoritePanels]);

    const tabPanes = [];
    panelsAsArrayWithId.forEach((el, idx) => {
        const panel = forumPanels[el.id];
        if (panel) {
            tabPanes.push(
                <Box key={idx} ref={refs.current[idx]}>
                    <PanelItem item={panel} id={el.id} />
                </Box>
            );
        } else {
            removeFromFavorites(el.id);
        }
    });

    const getHeightOffset = (refHeights, panels) => {
        const currentDateAndTime = moment();

        let heightOffset = 0;
        //const currentDateAndTime = moment("2020-04-28 22:01:01.0");  // This was left for testing purposes
        for (let i = 0; i < panels.length; i += 1) {
            const panel = panels[i];
            const start = moment(panel.startTime);
            const end = moment(panel.endTime);
            if (currentDateAndTime.isBetween(start, end) || currentDateAndTime.isBefore(start)) {
                return heightOffset;
            } else {
                heightOffset += refHeights[i];
            }
        }
        return 0;
    };

    useEffect(() => {
        if (favoritePanels.length > 0) {
            const refHeights = refs.current
                .filter(el => {
                    return el.current !== null;
                })
                .map(ref => ref.current.getBoundingClientRect().height);
            const heightOffset = getHeightOffset(refHeights, panelsAsArrayWithId);
            panelsContainerRef.current.scrollBy({ top: heightOffset, behavior: "smooth" });
        }
    }, [favoritePanels, panelsAsArrayWithId]);

    return (
        <>
            <TitleSeparator back className="custom-height">
                {strings.favoritePanels.HEADER}
            </TitleSeparator>
            {favoritePanels.length > 0 && <StyledPanelsContainer ref={panelsContainerRef}>{tabPanes}</StyledPanelsContainer>}
            {favoritePanels.length === 0 && (
                <StyledPanelsContainer>
                    <StyledContainer sx={{ mx: [3, 4] }}>
                        <StyledHeaderLabel fontWeight="bold" fontSize={[4, 5]} padding={3}>
                            {strings.panel.NO_FAVORITE_PANELS}
                        </StyledHeaderLabel>
                    </StyledContainer>
                </StyledPanelsContainer>
            )}
        </>
    );
};

export default withLocalizeStrings(FavouritePanels);
